import { infinityCards as fallbackInfinityCards } from "../game/infinityCards";
import { WHISPERING_FOREST_STORY_PACK_REWARD_IDS } from "../../components/mapScenes/whisperingForest/storyPackChests";

export const STORY_MERCHANT_UNLOCK_MISSION_ID = "act-1-mission-10";
export const STORY_MERCHANT_SELL_RATE = 0.25;

const STORY_MERCHANT_PRICE_BY_ID = Object.freeze({
  "08ahgmfrUXkqjuZrw5YW": 200,
  "1t9ruhC7mRj5vVhqyzjO": 95,
  "3TW3qkAKIOrhY2iGsCRq": 75,
  "4XYtsS8XCnoMDWGZO1dL": 95,
  "4igatDDkNcM2FN5N4iBM": 200,
  CMigdt1VIX3kygZeea0H: 75,
  E3V9pe7yNhajAK1nhQJ2: 60,
  Fki1kjjWd2nYbaC5sXww: 200,
  NquvLD7GdkIi6L2L7tUd: 45,
  O4yQTGdOjA1s6hnI1P4d: 60,
  QbbQAhH9rT9zTRKpgvAj: 125,
  R3HylH1GS1orDq3bqN5Q: 60,
  V8AFiagDbeR8W44hweO1: 60,
});

const normalizeCatalogId = (sourceId = "") =>
  String(sourceId || "").trim().toLowerCase();

const merchantRewardIdSet = new Set(
  WHISPERING_FOREST_STORY_PACK_REWARD_IDS.map(normalizeCatalogId),
);

const storyMerchantPriceByNormalizedId = Object.freeze(
  Object.entries(STORY_MERCHANT_PRICE_BY_ID).reduce((lookup, [sourceId, price]) => {
    lookup[normalizeCatalogId(sourceId)] = price;
    return lookup;
  }, {}),
);

const normalizeMissionNumber = (progress = {}) => {
  const missionNumber = Number(progress?.currentMission);
  if (Number.isFinite(missionNumber)) return missionNumber;

  const match = String(progress?.currentMissionId || "").match(
    /^act-(\d+)-mission-(\d+)$/,
  );
  return match ? Number(match[2]) : 0;
};

const normalizeActNumber = (progress = {}) => {
  const actNumber = Number(progress?.currentAct ?? progress?.actNumber);
  if (Number.isFinite(actNumber)) return actNumber;

  const match = String(progress?.currentMissionId || "").match(
    /^act-(\d+)-mission-(\d+)$/,
  );
  return match ? Number(match[1]) : 0;
};

export const hasCompletedStoryMerchantUnlock = (progress = {}) => {
  if (progress?.lastCompletedMissionId === STORY_MERCHANT_UNLOCK_MISSION_ID) {
    return true;
  }

  const actNumber = normalizeActNumber(progress);
  const missionNumber = normalizeMissionNumber(progress);

  if (actNumber > 1) return true;
  if (actNumber === 1 && missionNumber > 10) return true;

  return progress?.status === "completed";
};

export const getStoryMerchantPrice = (sourceId = "") =>
  storyMerchantPriceByNormalizedId[normalizeCatalogId(sourceId)] || null;

export const getStoryMerchantSellPrice = (sourceId = "") => {
  const purchasePrice = getStoryMerchantPrice(sourceId);
  if (!purchasePrice) return null;

  return Math.max(
    5,
    Math.round((purchasePrice * STORY_MERCHANT_SELL_RATE) / 5) * 5,
  );
};

export const isStoryMerchantCatalogItem = (item = {}) =>
  item?.kind === "infinityCard" &&
  merchantRewardIdSet.has(normalizeCatalogId(item?.sourceId || item?.id));

export const isStoryMerchantSellableItem = (item = {}) =>
  isStoryMerchantCatalogItem(item) && Number(item?.quantity || 0) > 0;

export const getStoryMerchantCatalog = (catalogCards = []) => {
  const cardsById = [...fallbackInfinityCards, ...(catalogCards || [])].reduce(
    (lookup, card) => {
      const id = String(card?.id || "");
      if (merchantRewardIdSet.has(normalizeCatalogId(id))) {
        lookup.set(normalizeCatalogId(id), card);
      }
      return lookup;
    },
    new Map(),
  );

  return WHISPERING_FOREST_STORY_PACK_REWARD_IDS.map((sourceId) => {
    const card = cardsById.get(normalizeCatalogId(sourceId));
    const price = getStoryMerchantPrice(sourceId);
    if (!card || !price) return null;

    return {
      ...card,
      kind: "infinityCard",
      sourceId: String(sourceId),
      price,
      sellPrice: getStoryMerchantSellPrice(sourceId),
    };
  })
    .filter(Boolean)
    .sort((left, right) => left.price - right.price || left.name.localeCompare(right.name));
};

export const getStoryMerchantCatalogItem = (sourceId = "") =>
  getStoryMerchantCatalog().find(
    (item) => normalizeCatalogId(item.sourceId) === normalizeCatalogId(sourceId),
  ) || null;
